import React, { useState } from "react";
import {
    Box,
    Button,
    Grid,
    Paper,
    Typography,
    Link as MuiLink,
    CircularProgress,
    Backdrop,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { urls } from "../../../common/urls";
import { useFetchContext } from "../../../common/FetchContext";

export const ProjectDownloadReport = ({ data, projectNumber, handleclose }: any) => {

    const [isDownloading, setIsDownloading] = useState(false);

    const [selectedBOMIndex, setSelectedBOMIndex] = useState<number | null>(null);
    const [selectedSerials, setSelectedSerials] = useState<string[]>([]);
    const [selectedSequences, setSelectedSequences] = useState<string[]>([]);
    const [selectedProcedures, setSelectedProcedures] = useState<string[]>([]);

    const [serialExpanded, setSerialExpanded] = useState(false);
    const [sequenceExpanded, setSequenceExpanded] = useState(false);
    const [procedureExpanded, setProcedureExpanded] = useState(false);
    const { authAxios } = useFetchContext();


    // Toggle serial selection
    const handleSerialToggle = (serial: string) => {
        setSelectedSerials((prev) => {
            let updated: string[];

            if (prev.includes(serial)) {
                // Deselect case
                updated = prev.filter((s) => s !== serial);

                // Clear dependent selections when serials change
                setSelectedSequences([]);
                setSelectedProcedures([]);

                setSequenceExpanded(false);
                setProcedureExpanded(false);
            } else {
                // Select case
                updated = [...prev, serial];

                setSelectedSequences([]);
                setSelectedProcedures([]);

                setSequenceExpanded(false);
                setProcedureExpanded(false);


            }

            return updated;
        });
    };


    // Toggle sequence (procedure_name) selection
    const handleSequenceToggle = (sequence: string) => {
        setSelectedSequences((prev) => {
            let updated: string[];

            if (prev.includes(sequence)) {
                // Deselect case
                updated = prev.filter((s) => s !== sequence);

                // Clear dependent procedures when sequences change
                setSelectedProcedures([]);
                setProcedureExpanded(false)
            } else {
                // Select case
                updated = [...prev, sequence];

                // Also clear procedures since new sequences may have different ones
                setSelectedProcedures([]);
                setProcedureExpanded(false);
            }

            return updated;
        });
    };


    // Toggle procedure_definition_name selection
    const handleProcedureToggle = (procedure: string) => {
        setSelectedProcedures((prev) =>
            prev.includes(procedure)
                ? prev.filter((p) => p !== procedure)
                : [...prev, procedure]
        );
    };


    const handleDownload = async () => {
        if (selectedBOMIndex === null) {
            alert("Please select a BOM first.");
            return;
        }

        const selectedBOM = data[selectedBOMIndex];

        const payload = {
            workorder_id: selectedBOM.workorder_id,
            serial_numbers: selectedSerials,
            procedure_names: selectedSequences,
            procedure_definition_id: (() => {
                const selectedDetails =
                    data[selectedBOMIndex]?.details.filter((d: any) =>
                        selectedSerials.includes(d.serial_numbers)
                    ) || [];

                const allProceduresPerSerial = selectedDetails.map((d: any) => {
                    return d.procedures
                        .filter((p: any) =>
                            selectedSequences.includes(p.procedure_name)
                        )
                        .flatMap(
                            (p: any) => p.procedure_definition_details || []
                        )
                        .map((p: any) => ({
                            id: p.procedure_definition_id,
                            name: p.procedure_definition_name,
                        }));
                });

                // Find common procedures across selected serials
                let commonProcedures: { id: number; name: string }[] = [];
                if (allProceduresPerSerial.length > 0) {
                    commonProcedures = allProceduresPerSerial.reduce(
                        (a: any[], b: any[]) =>
                            a.filter((x) => b.some((y) => y.id === x.id)),
                        allProceduresPerSerial[0]
                    );
                }

                // Filter only selected ones
                const selectedDefs = commonProcedures.filter((proc) =>
                    selectedProcedures.includes(proc.name)
                );

                return selectedDefs.map((p) => p.id);
            })(),
        };

        console.log("Download Payload:", payload);

        setIsDownloading(true);

        try {
            // Use your authAxios instance
            const response = await authAxios.post(
                urls.reportWriterAgenda.project_download(projectNumber),
                payload,
                { responseType: "blob" }
            );

            const headerContentType = String(response.headers["content-type"] || "");

            const blob = new Blob([response.data], {
                type: headerContentType,
            });
            const downloadUrl = window.URL.createObjectURL(blob);

            // Timestamp for filename
            const now = new Date();
            const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;

            // Use first selected procedure for filename
            const firstProcedureName = selectedProcedures[0] || "Procedure";

            // Determine file extension
            const contentType = headerContentType.toLowerCase();
            const extension = contentType.includes("zip") ? "zip" : "xlsx";

            const fileName = `${projectNumber}_${selectedBOM.workorder_name}_${firstProcedureName}_${timestamp}.${extension}`;

            // Trigger download
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setIsDownloading(false);

            setSelectedBOMIndex(null);
            setSelectedSerials([]);
            setSelectedSequences([]);
            setSelectedProcedures([]);

            setSerialExpanded(false);
            setSequenceExpanded(false);
            setProcedureExpanded(false);

            handleclose();

        } catch (error: any) {
            console.error("Download failed:", error);
            alert("Download failed. Please try again.");
            setIsDownloading(false);
        }
    };


    return (
        <>

            {/* <Backdrop open={isDownloading} style={{ zIndex: 1300, color: "#fff" }}>
                <CircularProgress color="inherit" />
            </Backdrop> */}
            
            {/* Select BOM */}
            <Paper elevation={2} style={{ padding: "10px", margin: "10px" }}>
                <Grid container spacing={1} direction="column">
                    <Grid item>
                        <Typography
                            variant="subtitle1"
                            style={{ color: "#800280", fontSize: "18px" }}
                        >
                            Select BOM
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Grid container spacing={1}>
                            {data.map((item: any, index: number) => (
                                <Grid item key={item.workorder_id}>
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent={
                                            selectedBOMIndex === index
                                                ? "flex-start"
                                                : "center"
                                        }
                                        onClick={() => {
                                            setSelectedBOMIndex(index);
                                            setSelectedSerials([]);
                                            setSelectedSequences([]);
                                            setSelectedProcedures([]);

                                            setSerialExpanded(false);
                                            setSequenceExpanded(false);
                                            setProcedureExpanded(false);
                                        }}
                                        style={{
                                            minWidth: 100,
                                            padding: "6px 12px",
                                            border:
                                                selectedBOMIndex === index
                                                    ? "1px solid #FFD27F"
                                                    : "1px solid #ccc",
                                            backgroundColor:
                                                selectedBOMIndex === index
                                                    ? "#FFD27F"
                                                    : "transparent",
                                            color: "#000",
                                            borderRadius: 10,
                                            cursor: "pointer",
                                            display: "flex",
                                            gap: selectedBOMIndex === index ? 4 : 0,
                                            userSelect: "none",
                                        }}
                                    >
                                        {selectedBOMIndex === index && (
                                            <CheckIcon fontSize="small" />
                                        )}
                                        <span
                                            style={{
                                                flex: 1,
                                                textAlign:
                                                    selectedBOMIndex === index
                                                        ? "left"
                                                        : "center",
                                            }}
                                        >
                                            {item.workorder_name}
                                        </span>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Select Serial Numbers */}
            {selectedBOMIndex !== null && (
                <Paper elevation={2} style={{ padding: "10px", margin: "10px" }}>
                    <Grid container spacing={1} direction="column">
                        <Grid item xs={12} container alignItems="center" spacing={3}>
                            <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ color: "#800280", fontSize: "16px" }}
                                        >
                                            Select Serial Numbers
                                        </Typography>
                                    </Grid>
                                    {serialExpanded && (
                                        <Grid item>
                                            <Typography
                                                variant="body2"
                                                style={{ color: "#800280", fontSize: "16px" }}
                                            >
                                                ({selectedSerials.length} selected of{" "}
                                                {data[selectedBOMIndex]?.details.length || 0})
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>


                            {serialExpanded && (
                                <Grid item >
                                    <MuiLink
                                        style={{ cursor: "pointer", fontSize: 14 }}
                                        onClick={() => {
                                            const allSerials =
                                                data[selectedBOMIndex]?.details.map(
                                                    (d: any) => d.serial_numbers
                                                ) || [];
                                            if (selectedSerials.length === allSerials.length) {
                                                setSelectedSerials([]);
                                            } else {
                                                setSelectedSerials(allSerials);
                                            }
                                        }}
                                    >
                                        {selectedSerials.length ===
                                            data[selectedBOMIndex]?.details.length
                                            ? "Deselect All"
                                            : "Select All"}
                                    </MuiLink>
                                </Grid>
                            )}
                            <Grid item style={{ marginLeft: "auto" }}>
                                <MuiLink
                                    style={{ cursor: "pointer", fontSize: 14 }}
                                    onClick={() => setSerialExpanded(!serialExpanded)}
                                >
                                    {serialExpanded ? "Hide All" : "View All"}
                                </MuiLink>
                            </Grid>
                        </Grid>

                        {serialExpanded && (
                            <Grid item>
                                <Grid container spacing={1}>
                                    {data[selectedBOMIndex]?.details.map(
                                        (detail: any) => {
                                            const serial = detail.serial_numbers;
                                            const isSelected =
                                                selectedSerials.includes(serial);
                                            return (
                                                <Grid item key={serial}>
                                                    <Grid
                                                        container
                                                        alignItems="center"
                                                        justifyContent={
                                                            isSelected ? "flex-start" : "center"
                                                        }
                                                        onClick={() =>
                                                            handleSerialToggle(serial)
                                                        }
                                                        style={{
                                                            minWidth: 100,
                                                            padding: "6px 12px",
                                                            border: isSelected
                                                                ? "1px solid #FFD27F"
                                                                : "1px solid #ccc",
                                                            backgroundColor: isSelected
                                                                ? "#FFD27F"
                                                                : "transparent",
                                                            color: "#000",
                                                            borderRadius: 10,
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            gap: isSelected ? 4 : 0,
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        {isSelected && (
                                                            <CheckIcon fontSize="small" />
                                                        )}
                                                        <span
                                                            style={{
                                                                flex: 1,
                                                                textAlign: isSelected
                                                                    ? "left"
                                                                    : "center",
                                                            }}
                                                        >
                                                            {serial}
                                                        </span>
                                                    </Grid>
                                                </Grid>
                                            );
                                        }
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            )}

            {/* Select Sequence */}
            {selectedBOMIndex !== null && selectedSerials.length > 0 && (
                <Paper elevation={2} style={{ padding: "10px", margin: "10px" }}>
                    <Grid container spacing={1} direction="column">

                        <Grid item xs={12} container alignItems="center" spacing={3}>
                            <Grid item >
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item >
                                        <Typography
                                            variant="subtitle1"
                                            style={{ color: "#800280", fontSize: "16px" }}
                                        >
                                            Select Sequence for Selected Serials
                                        </Typography>
                                    </Grid>

                                    {sequenceExpanded && (
                                        <Grid item>
                                            <Typography
                                                variant="body2"
                                                style={{ color: "#800280", fontSize: "16px" }}
                                            >
                                                ({selectedSequences.length} selected of {(() => {
                                                    const selectedDetails =
                                                        data[selectedBOMIndex]?.details.filter((d: any) =>
                                                            selectedSerials.includes(d.serial_numbers)
                                                        ) || [];

                                                    const allSequencesPerSerial = selectedDetails.map((d: any) =>
                                                        d.procedures.map((p: any) => p.procedure_name)
                                                    );

                                                    const commonSequences =
                                                        allSequencesPerSerial.length > 0
                                                            ? allSequencesPerSerial.reduce((a: string[], b: string[]) =>
                                                                a.filter((x) => b.includes(x))
                                                            )
                                                            : [];

                                                    return commonSequences.length;
                                                })()})
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>


                            {sequenceExpanded && (
                                <Grid item>
                                    <MuiLink
                                        style={{ cursor: "pointer", fontSize: 14 }}
                                        onClick={() => {
                                            const selectedDetails =
                                                data[selectedBOMIndex]?.details.filter((d: any) =>
                                                    selectedSerials.includes(d.serial_numbers)
                                                ) || [];

                                            const allSequencesPerSerial = selectedDetails.map((d: any) =>
                                                d.procedures.map((p: any) => p.procedure_name)
                                            );

                                            // Find common procedure_names across all selected serials
                                            const commonSequences =
                                                allSequencesPerSerial.length > 0
                                                    ? allSequencesPerSerial.reduce((a: string[], b: string[]) =>
                                                        a.filter((x) => b.includes(x))
                                                    )
                                                    : [];

                                            if (selectedSequences.length === commonSequences.length) {
                                                setSelectedSequences([]);
                                            } else {
                                                setSelectedSequences(commonSequences);
                                            }
                                        }}
                                    >
                                        {(() => {
                                            const selectedDetails =
                                                data[selectedBOMIndex]?.details.filter((d: any) =>
                                                    selectedSerials.includes(d.serial_numbers)
                                                ) || [];
                                            const allSequencesPerSerial = selectedDetails.map((d: any) =>
                                                d.procedures.map((p: any) => p.procedure_name)
                                            );
                                            const commonSequences =
                                                allSequencesPerSerial.length > 0
                                                    ? allSequencesPerSerial.reduce((a: string[], b: string[]) =>
                                                        a.filter((x) => b.includes(x))
                                                    )
                                                    : [];
                                            return selectedSequences.length === commonSequences.length
                                                ? "Deselect All"
                                                : "Select All";
                                        })()}
                                    </MuiLink>
                                </Grid>
                            )}

                            <Grid item style={{ marginLeft: "auto" }}>
                                <MuiLink
                                    style={{ cursor: "pointer", fontSize: 14 }}
                                    onClick={() => setSequenceExpanded(!sequenceExpanded)}
                                >
                                    {sequenceExpanded ? "Hide All" : "View All"}
                                </MuiLink>
                            </Grid>
                        </Grid>

                        {sequenceExpanded && (
                            <Grid item>
                                <Grid container spacing={1}>
                                    {(() => {
                                        const selectedDetails =
                                            data[selectedBOMIndex]?.details.filter((d: any) =>
                                                selectedSerials.includes(d.serial_numbers)
                                            ) || [];

                                        // Get all procedure_name arrays for each selected serial
                                        const allSequencesPerSerial = selectedDetails.map((d: any) =>
                                            d.procedures.map((p: any) => p.procedure_name)
                                        );

                                        // Find common procedure_names across all selected serials
                                        const commonSequences =
                                            allSequencesPerSerial.length > 0
                                                ? allSequencesPerSerial.reduce((a: string[], b: string[]) =>
                                                    a.filter((x) => b.includes(x))
                                                )
                                                : [];

                                        return commonSequences.map((sequence: string) => {
                                            const isSelected = selectedSequences.includes(sequence);
                                            return (
                                                <Grid item key={sequence}>
                                                    <Grid
                                                        container
                                                        alignItems="center"
                                                        justifyContent={
                                                            isSelected ? "flex-start" : "center"
                                                        }
                                                        onClick={() => handleSequenceToggle(sequence)}
                                                        style={{
                                                            minWidth: 150,
                                                            padding: "6px 12px",
                                                            border: isSelected
                                                                ? "1px solid #FFD27F"
                                                                : "1px solid #ccc",
                                                            backgroundColor: isSelected
                                                                ? "#FFD27F"
                                                                : "transparent",
                                                            color: "#000",
                                                            borderRadius: 10,
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            gap: isSelected ? 4 : 0,
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        {isSelected && <CheckIcon fontSize="small" />}
                                                        <span
                                                            style={{
                                                                flex: 1,
                                                                textAlign: isSelected ? "left" : "center",
                                                            }}
                                                        >
                                                            {sequence}
                                                        </span>
                                                    </Grid>
                                                </Grid>
                                            );
                                        });
                                    })()}
                                </Grid>
                            </Grid>
                        )}


                    </Grid>
                </Paper>
            )}

            {/* Select Procedure */}
            {selectedBOMIndex !== null && selectedSequences.length > 0 && (
                <Paper elevation={2} style={{ padding: "10px", margin: "10px" }}>
                    <Grid container spacing={1} direction="column">

                        <Grid item xs={12} container alignItems="center" spacing={3}>
                            <Grid item>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item >
                                        <Typography
                                            variant="subtitle1"
                                            style={{ color: "#800280", fontSize: "16px" }}
                                        >
                                            Select Procedure For Selected Sequence
                                        </Typography>
                                    </Grid>
                                    {procedureExpanded && (
                                        <Grid item>
                                            <Typography
                                                variant="body2"
                                                style={{ color: "#800280", fontSize: "16px" }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    style={{ color: "#800280", fontSize: "14px" }}
                                                >
                                                    ({selectedProcedures.length} selected of {(() => {
                                                        const selectedDetails =
                                                            data[selectedBOMIndex]?.details.filter((d: any) =>
                                                                selectedSerials.includes(d.serial_numbers)
                                                            ) || [];

                                                        const allProceduresPerSerial = selectedDetails.map((d: any) =>
                                                            d.procedures
                                                                .filter((p: any) => selectedSequences.includes(p.procedure_name))
                                                                .flatMap((p: any) => p.procedure_definition_details || [])
                                                                .map((p: any) => p.procedure_definition_name)
                                                        );

                                                        const commonProcedures =
                                                            allProceduresPerSerial.length > 0
                                                                ? allProceduresPerSerial.reduce((a: string[], b: string[]) =>
                                                                    a.filter((x) => b.includes(x))
                                                                )
                                                                : [];

                                                        return commonProcedures.length;
                                                    })()})
                                                </Typography>
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Grid>


                            {/* ✅ Select / Deselect All Link (shown only when expanded) */}
                            {procedureExpanded && (
                                <Grid item>
                                    <MuiLink
                                        style={{ cursor: "pointer", fontSize: 14 }}
                                        onClick={() => {
                                            const selectedDetails =
                                                data[selectedBOMIndex]?.details.filter((d: any) =>
                                                    selectedSerials.includes(d.serial_numbers)
                                                ) || [];

                                            // Collect all procedures per serial for selected sequences
                                            const allProceduresPerSerial = selectedDetails.map((d: any) => {
                                                return d.procedures
                                                    .filter((p: any) =>
                                                        selectedSequences.includes(p.procedure_name)
                                                    )
                                                    .flatMap(
                                                        (p: any) => p.procedure_definition_details || []
                                                    )
                                                    .map((p: any) => ({
                                                        id: p.procedure_definition_id,
                                                        name: p.procedure_definition_name,
                                                    }));
                                            });

                                            // Find common procedures across selected serials
                                            let commonProcedures: { id: string; name: string }[] = [];
                                            if (allProceduresPerSerial.length > 0) {
                                                commonProcedures = allProceduresPerSerial.reduce(
                                                    (a: any[], b: any[]) =>
                                                        a.filter((x) => b.some((y) => y.id === x.id)),
                                                    allProceduresPerSerial[0]
                                                );
                                            }

                                            // Remove duplicates
                                            const uniqueProcedures = Array.from(
                                                new Map(commonProcedures.map((p) => [p.id, p])).values()
                                            );

                                            // Toggle logic
                                            if (selectedProcedures.length === uniqueProcedures.length) {
                                                setSelectedProcedures([]);
                                            } else {
                                                setSelectedProcedures(uniqueProcedures.map((p) => p.name));
                                            }
                                        }}
                                    >
                                        {(() => {
                                            const selectedDetails =
                                                data[selectedBOMIndex]?.details.filter((d: any) =>
                                                    selectedSerials.includes(d.serial_numbers)
                                                ) || [];

                                            const allProceduresPerSerial = selectedDetails.map((d: any) => {
                                                return d.procedures
                                                    .filter((p: any) =>
                                                        selectedSequences.includes(p.procedure_name)
                                                    )
                                                    .flatMap(
                                                        (p: any) => p.procedure_definition_details || []
                                                    )
                                                    .map((p: any) => ({
                                                        id: p.procedure_definition_id,
                                                        name: p.procedure_definition_name,
                                                    }));
                                            });

                                            let commonProcedures: { id: string; name: string }[] = [];
                                            if (allProceduresPerSerial.length > 0) {
                                                commonProcedures = allProceduresPerSerial.reduce(
                                                    (a: any[], b: any[]) =>
                                                        a.filter((x) => b.some((y) => y.id === x.id)),
                                                    allProceduresPerSerial[0]
                                                );
                                            }

                                            const uniqueProcedures = Array.from(
                                                new Map(commonProcedures.map((p) => [p.id, p])).values()
                                            );

                                            return selectedProcedures.length === uniqueProcedures.length
                                                ? "Deselect All"
                                                : "Select All";
                                        })()}
                                    </MuiLink>
                                </Grid>
                            )}

                            <Grid item style={{ marginLeft: "auto" }}>
                                <MuiLink
                                    style={{ cursor: "pointer", fontSize: 14 }}
                                    onClick={() => setProcedureExpanded(!procedureExpanded)}
                                >
                                    {procedureExpanded ? "Hide All" : "View All"}
                                </MuiLink>
                            </Grid>
                        </Grid>


                        {procedureExpanded && (
                            <Grid item>
                                <Grid container spacing={1}>
                                    {(() => {
                                        const selectedDetails =
                                            data[selectedBOMIndex]?.details.filter((d: any) =>
                                                selectedSerials.includes(d.serial_numbers)
                                            ) || [];

                                        // Collect all procedures per serial for selected sequences
                                        const allProceduresPerSerial = selectedDetails.map((d: any) => {
                                            return d.procedures
                                                .filter((p: any) =>
                                                    selectedSequences.includes(p.procedure_name)
                                                )
                                                .flatMap(
                                                    (p: any) =>
                                                        p.procedure_definition_details || []
                                                )
                                                .map((p: any) => ({
                                                    id: p.procedure_definition_id,
                                                    name: p.procedure_definition_name,
                                                }));
                                        });

                                        // Find common procedure_definition_details across selected serials
                                        let commonProcedures: { id: string; name: string }[] = [];
                                        if (allProceduresPerSerial.length > 0) {
                                            commonProcedures = allProceduresPerSerial.reduce(
                                                (a: any[], b: any[]) =>
                                                    a.filter((x) => b.some((y) => y.id === x.id)),
                                                allProceduresPerSerial[0]
                                            );
                                        }

                                        // Remove duplicates (if any)
                                        const uniqueProcedures = Array.from(
                                            new Map(commonProcedures.map((p) => [p.id, p])).values()
                                        );

                                        return uniqueProcedures.map((item: any) => {
                                            const isSelected = selectedProcedures.includes(item.name);
                                            return (
                                                <Grid item key={item.id}>
                                                    <Grid
                                                        container
                                                        alignItems="center"
                                                        justifyContent={
                                                            isSelected ? "flex-start" : "center"
                                                        }
                                                        onClick={() =>
                                                            handleProcedureToggle(item.name)
                                                        }
                                                        style={{
                                                            minWidth: 200,
                                                            padding: "6px 12px",
                                                            border: isSelected
                                                                ? "1px solid #FFD27F"
                                                                : "1px solid #ccc",
                                                            backgroundColor: isSelected
                                                                ? "#FFD27F"
                                                                : "transparent",
                                                            color: "#000",
                                                            borderRadius: 10,
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            gap: isSelected ? 4 : 0,
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        {isSelected && <CheckIcon fontSize="small" />}
                                                        <span
                                                            style={{
                                                                flex: 1,
                                                                textAlign: isSelected ? "left" : "center",
                                                            }}
                                                        >
                                                            {item.name}
                                                        </span>
                                                    </Grid>
                                                </Grid>
                                            );
                                        });
                                    })()}
                                </Grid>
                            </Grid>
                        )}

                    </Grid>
                </Paper>
            )}

            {/* Download Button */}
            <Grid
                container
                justifyContent="center"
                spacing={2}
                style={{ marginTop: 24, marginBottom: 24 }}
            >
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDownload}
                        disabled={
                            selectedBOMIndex === null || isDownloading
                        }
                    >
                        {isDownloading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : ("Download")}
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};
